import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { 
  collection, 
  getDocs, 
  addDoc, 
  updateDoc, 
  deleteDoc, 
  doc, 
  query, 
  orderBy 
} from 'firebase/firestore';
import { ref, uploadBytes, getDownloadURL, deleteObject } from 'firebase/storage';
import { db, storage } from '../../firebase';
import logger from '../../utils/logger';
import { 
  FaTrophy, 
  FaPlus, 
  FaEdit, 
  FaTrash, 
  FaSave, 
  FaTimes,
  FaImage,
  FaStar,
  FaGem,
  FaCrown
} from 'react-icons/fa';
import toast from 'react-hot-toast';

const AdminBadges = () => {
  const [badges, setBadges] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [editingBadge, setEditingBadge] = useState(null);
  const [uploading, setUploading] = useState(false);
  const [badgeForm, setBadgeForm] = useState({
    nom: '',
    description: '',
    icone: '',
    couleur: '#3B82F6',
    rarete: 'commun',
    points: 10,
    conditions: '',
    actif: true
  });

  const raretes = [
    { value: 'commun', label: 'Commun', color: 'text-gray-600', icon: FaStar },
    { value: 'rare', label: 'Rare', color: 'text-blue-600', icon: FaGem },
    { value: 'epique', label: 'Épique', color: 'text-purple-600', icon: FaTrophy },
    { value: 'legendaire', label: 'Légendaire', color: 'text-yellow-600', icon: FaCrown }
  ];

  useEffect(() => {
    loadBadges();
  }, []);

  const loadBadges = async () => {
    try {
      setLoading(true);
      const q = query(collection(db, 'badges'), orderBy('dateCreation', 'desc'));
      const snapshot = await getDocs(q);
      const badgesData = snapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data(),
        dateCreation: doc.data().dateCreation?.toDate()
      }));
      setBadges(badgesData);
    } catch (error) {
      logger.error('Erreur loadBadges:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleImageUpload = async (file) => {
    if (!file) return null;
    
    try {
      setUploading(true);
      const imageRef = ref(storage, `badges/${Date.now()}_${file.name}`);
      const snapshot = await uploadBytes(imageRef, file);
      const downloadURL = await getDownloadURL(snapshot.ref);
      return downloadURL;
    } catch (error) {
      logger.error('Erreur upload image:', error);
      toast.error('Erreur lors de l\'upload de l\'image', {
        style: {
          background: '#001B56',
          color: '#FFE400',
        }
      });
      return null;
    } finally {
      setUploading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!badgeForm.nom.trim() || !badgeForm.description.trim()) {
      toast.error('Veuillez remplir tous les champs obligatoires', {
        icon: '⚠️',
        style: {
          background: '#001B56',
          color: '#FFE400',
        }
      });
      return;
    }

    try {
      const badgeData = {
        ...badgeForm,
        points: parseInt(badgeForm.points),
        dateModification: new Date()
      };

      if (editingBadge) {
        // Mise à jour
        await updateDoc(doc(db, 'badges', editingBadge.id), badgeData);
        toast.success('Badge mis à jour avec succès !', {
          icon: '🏅',
          style: {
            background: '#001B56',
            color: '#FFE400',
          }
        });
      } else {
        // Création
        badgeData.dateCreation = new Date();
        await addDoc(collection(db, 'badges'), badgeData);
        toast.success('Badge créé avec succès !', {
          icon: '🎖️',
          style: {
            background: '#001B56',
            color: '#FFE400',
          }
        });
      }

      resetForm();
      loadBadges();
    } catch (error) {
      logger.error('Erreur handleSubmit:', error);
      toast.error('Erreur lors de la sauvegarde', {
        style: {
          background: '#001B56',
          color: '#FFE400',
        }
      });
    }
  };

  const handleEdit = (badge) => {
    setEditingBadge(badge);
    setBadgeForm({
      nom: badge.nom,
      description: badge.description,
      icone: badge.icone || '',
      couleur: badge.couleur || '#3B82F6',
      rarete: badge.rarete || 'commun',
      points: badge.points || 10,
      conditions: badge.conditions || '',
      actif: badge.actif !== false
    });
    setShowModal(true);
  };

  const handleDelete = async (badgeId) => {
    if (!window.confirm('Êtes-vous sûr de vouloir supprimer ce badge ?')) {
      return;
    }

    try {
      await deleteDoc(doc(db, 'badges', badgeId));
      toast.success('Badge supprimé avec succès !', {
        icon: '🗑️',
        style: {
          background: '#001B56',
          color: '#FFE400',
        }
      });
      loadBadges();
    } catch (error) {
      logger.error('Erreur handleDelete:', error);
      toast.error('Erreur lors de la suppression', {
        style: {
          background: '#001B56',
          color: '#FFE400',
        }
      });
    }
  };

  const resetForm = () => {
    setBadgeForm({
      nom: '',
      description: '',
      icone: '',
      couleur: '#3B82F6',
      rarete: 'commun',
      points: 10,
      conditions: '',
      actif: true
    });
    setEditingBadge(null);
    setShowModal(false);
  };

  const getRareteInfo = (rarete) => {
    return raretes.find(r => r.value === rarete) || raretes[0];
  };

  const getRareteColor = (rarete) => {
    switch (rarete) {
      case 'commun': return 'bg-gray-100 text-gray-800';
      case 'rare': return 'bg-blue-100 text-blue-800';
      case 'epique': return 'bg-purple-100 text-purple-800';
      case 'legendaire': return 'bg-yellow-100 text-yellow-800';
      default: return 'bg-gray-100 text-gray-800';
    }
  };

  return (
    <div className="p-6">
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        className="mb-8"
      >
        <div className="flex items-center justify-between mb-4">
          <div className="flex items-center gap-3">
            <FaTrophy className="text-3xl text-yellow-600" />
            <h1 className="text-3xl font-bold">Gestion des Badges</h1>
          </div>
          <button
            onClick={() => setShowModal(true)}
            className="flex items-center gap-2 px-6 py-3 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
          >
            <FaPlus />
            Nouveau Badge
          </button>
        </div>
        <p className="text-gray-600">
          Créez et gérez les badges que les utilisateurs peuvent obtenir
        </p>
      </motion.div>

      {/* Liste des badges */}
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 0.1 }}
        className="bg-white rounded-xl shadow-lg overflow-hidden"
      >
        {loading ? (
          <div className="text-center py-12">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto"></div>
            <p className="mt-4 text-gray-600">Chargement des badges...</p>
          </div>
        ) : badges.length === 0 ? (
          <div className="text-center py-12">
            <FaTrophy className="mx-auto text-6xl text-gray-400 mb-4" />
            <h3 className="text-xl font-bold mb-2">Aucun badge</h3>
            <p className="text-gray-600">Créez votre premier badge pour commencer !</p>
          </div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 p-6">
            {badges.map((badge, index) => (
              <motion.div
                key={badge.id}
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: index * 0.1 }}
                className="border border-gray-200 rounded-xl p-6 hover:shadow-lg transition-shadow"
              >
                <div className="flex items-start justify-between mb-4">
                  <div className="flex items-center gap-3">
                    {badge.icone ? (
                      <img 
                        src={badge.icone} 
                        alt={badge.nom}
                        className="w-12 h-12 rounded-lg object-cover"
                      />
                    ) : (
                      <div 
                        className="w-12 h-12 rounded-lg flex items-center justify-center text-white"
                        style={{ backgroundColor: badge.couleur }}
                      >
                        <FaTrophy className="text-xl" />
                      </div>
                    )}
                    <div>
                      <h3 className="font-bold text-lg">{badge.nom}</h3>
                      <span className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ${getRareteColor(badge.rarete)}`}>
                        {getRareteInfo(badge.rarete).label}
                      </span>
                    </div>
                  </div>
                  
                  <div className="flex items-center gap-2">
                    <button
                      onClick={() => handleEdit(badge)}
                      className="p-2 text-blue-600 hover:bg-blue-50 rounded-lg transition-colors"
                      title="Modifier"
                    >
                      <FaEdit />
                    </button>
                    <button
                      onClick={() => handleDelete(badge.id)}
                      className="p-2 text-red-600 hover:bg-red-50 rounded-lg transition-colors"
                      title="Supprimer"
                    >
                      <FaTrash />
                    </button>
                  </div>
                </div>

                <p className="text-gray-600 mb-4">{badge.description}</p>

                <div className="space-y-2 text-sm">
                  <div className="flex justify-between">
                    <span className="text-gray-500">Points:</span>
                    <span className="font-semibold">{badge.points}</span>
                  </div>
                  {badge.conditions && (
                    <div>
                      <span className="text-gray-500">Conditions:</span>
                      <p className="text-xs text-gray-600 mt-1">{badge.conditions}</p>
                    </div>
                  )}
                  <div className="flex justify-between">
                    <span className="text-gray-500">Statut:</span>
                    <span className={`text-xs px-2 py-1 rounded-full ${
                      badge.actif ? 'bg-green-100 text-green-800' : 'bg-red-100 text-red-800'
                    }`}>
                      {badge.actif ? 'Actif' : 'Inactif'}
                    </span>
                  </div>
                </div>
              </motion.div>
            ))}
          </div>
        )}
      </motion.div>

      {/* Modal d'ajout/édition */}
      <AnimatePresence>
        {showModal && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4"
          >
            <motion.div
              initial={{ scale: 0.9 }}
              animate={{ scale: 1 }}
              exit={{ scale: 0.9 }}
              className="bg-white rounded-xl max-w-2xl w-full max-h-[90vh] overflow-y-auto"
            >
              <div className="p-6">
                <div className="flex items-center justify-between mb-6">
                  <h2 className="text-2xl font-bold">
                    {editingBadge ? 'Modifier le Badge' : 'Nouveau Badge'}
                  </h2>
                  <button
                    onClick={resetForm}
                    className="p-2 text-gray-400 hover:text-gray-600 transition-colors"
                  >
                    <FaTimes />
                  </button>
                </div>

                <form onSubmit={handleSubmit} className="space-y-6">
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        Nom du badge *
                      </label>
                      <input
                        type="text"
                        value={badgeForm.nom}
                        onChange={(e) => setBadgeForm(prev => ({ ...prev, nom: e.target.value }))}
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                        required
                      />
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        Points attribués
                      </label>
                      <input
                        type="number"
                        value={badgeForm.points}
                        onChange={(e) => setBadgeForm(prev => ({ ...prev, points: e.target.value }))}
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                        min="0"
                      />
                    </div>
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Description *
                    </label>
                    <textarea
                      value={badgeForm.description}
                      onChange={(e) => setBadgeForm(prev => ({ ...prev, description: e.target.value }))}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                      rows="3"
                      required
                    />
                  </div>

                  <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        Rareté
                      </label>
                      <select
                        value={badgeForm.rarete}
                        onChange={(e) => setBadgeForm(prev => ({ ...prev, rarete: e.target.value }))}
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                      >
                        {raretes.map(rarete => (
                          <option key={rarete.value} value={rarete.value}>
                            {rarete.label}
                          </option>
                        ))}
                      </select>
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        Couleur
                      </label>
                      <div className="flex items-center gap-3">
                        <input
                          type="color"
                          value={badgeForm.couleur}
                          onChange={(e) => setBadgeForm(prev => ({ ...prev, couleur: e.target.value }))}
                          className="w-12 h-10 rounded border border-gray-300"
                        />
                        <input
                          type="text"
                          value={badgeForm.couleur}
                          onChange={(e) => setBadgeForm(prev => ({ ...prev, couleur: e.target.value }))}
                          className="flex-1 px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                        />
                      </div>
                    </div>
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Icône/Image
                    </label>
                    <div className="flex items-center gap-4">
                      <input
                        type="file"
                        accept="image/*"
                        onChange={async (e) => {
                          const file = e.target.files[0];
                          if (file) {
                            const url = await handleImageUpload(file);
                            if (url) {
                              setBadgeForm(prev => ({ ...prev, icone: url }));
                            }
                          }
                        }}
                        className="hidden"
                        id="badge-image"
                      />
                      <label
                        htmlFor="badge-image"
                        className="flex items-center gap-2 px-4 py-2 bg-gray-100 text-gray-700 rounded-lg hover:bg-gray-200 cursor-pointer transition-colors"
                      >
                        <FaImage />
                        {uploading ? 'Upload...' : 'Choisir une image'}
                      </label>
                      {badgeForm.icone && (
                        <img 
                          src={badgeForm.icone} 
                          alt="Preview"
                          className="w-12 h-12 rounded-lg object-cover"
                        />
                      )}
                    </div>
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Conditions d'obtention
                    </label>
                    <textarea
                      value={badgeForm.conditions}
                      onChange={(e) => setBadgeForm(prev => ({ ...prev, conditions: e.target.value }))}
                      placeholder="Décrivez comment obtenir ce badge..."
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                      rows="2"
                    />
                  </div>

                  <div className="flex items-center">
                    <input
                      type="checkbox"
                      id="badge-actif"
                      checked={badgeForm.actif}
                      onChange={(e) => setBadgeForm(prev => ({ ...prev, actif: e.target.checked }))}
                      className="rounded border-gray-300 text-blue-600 shadow-sm focus:border-blue-300 focus:ring focus:ring-blue-200 focus:ring-opacity-50"
                    />
                    <label htmlFor="badge-actif" className="ml-2 text-sm text-gray-700">
                      Badge actif (peut être obtenu par les utilisateurs)
                    </label>
                  </div>

                  <div className="flex gap-4 pt-6 border-t border-gray-200">
                    <button
                      type="button"
                      onClick={resetForm}
                      className="flex-1 px-4 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 transition-colors"
                    >
                      Annuler
                    </button>
                    <button
                      type="submit"
                      disabled={uploading}
                      className="flex-1 flex items-center justify-center gap-2 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:bg-gray-300 disabled:cursor-not-allowed transition-colors"
                    >
                      <FaSave />
                      {editingBadge ? 'Mettre à jour' : 'Créer'}
                    </button>
                  </div>
                </form>
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
};

export default AdminBadges;